import type { Action } from "../actions.types";
import { isActionVisibleOnDashboard } from "./isActionVisibleOnDashboard";
import { isActionCompleted } from "./isActionCompleted";
import { isActionDisabledByDate } from "./isActionDisabledByDate";

/**
 * Get the count of incomplete actions that are visible on the dashboard.
 * Used for badge counts in navigation.
 *
 * When `completedPhaseIds` is provided, actions whose `program_phase` is in
 * the set are excluded — so incomplete actions belonging to phases the admin
 * has marked as completed do not inflate the badge.
 *
 * Actions outside their date window (before `available_date` or after
 * `closed_date`) are also excluded — the badge tracks what is actionable now,
 * not future or expired work.
 *
 * @param actions - Array of user actions
 * @param completedPhaseIds - Optional set of phase IDs whose actions should be excluded
 * @returns Number of incomplete, visible actions
 */
export function getIncompleteActionCount(
  actions: Action[],
  completedPhaseIds?: Set<number>
): number {
  if (!actions || actions.length === 0) return 0;

  return actions.filter((action) => {
    if (!isActionVisibleOnDashboard(action)) return false;
    if (isActionCompleted(action)) return false;
    if (isActionDisabledByDate(action)) return false;
    if (
      completedPhaseIds &&
      action.program_phase !== null &&
      completedPhaseIds.has(action.program_phase)
    ) {
      return false;
    }
    return true;
  }).length;
}
